import Close from "@mui/icons-material/Close";
import EditSquare from "@mui/icons-material/EditSquare";
import LibraryBooks from "@mui/icons-material/LibraryBooks";
import PrivacyTipOutlined from "@mui/icons-material/PrivacyTipOutlined";
import Route from "@mui/icons-material/Route";
import SearchIcon from "@mui/icons-material/Search";
import SupportAgent from "@mui/icons-material/SupportAgent";
import VerticalSplitOutlined from "@mui/icons-material/VerticalSplitOutlined";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { TextInput } from "components/TextInput";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type WheelEvent,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { openFrontChatWithAiBotBypass } from "util/openFrontChat";

import { ScoutConversationSettingsMenu } from "./ScoutConversationSettingsMenu";
import {
  SCOUT_INFO_PANEL_TABS,
  SCOUT_INFO_PANEL_WIDTH_PX,
  SCOUT_SIDE_RAIL_WIDTH_PX,
  ScoutInfoPanelContentBody,
  type ScoutInfoPanelTab,
  type ScoutSidePanelView,
} from "./ScoutInfoPanel";
import { ScoutThreadListSection, useScoutThreadsSearch, type ScoutThreadListItem } from "./Search";

const PANEL_HEADER_HEIGHT_SPACING = 7;

// Icons live per-surface (the tab-bar chips and the collapsed rail use
// different sizes) but are keyed by tab, so a new/renamed tab in
// SCOUT_INFO_PANEL_TABS won't compile until both maps are updated.
const SCOUT_INFO_TAB_CHIP_ICON: Record<ScoutInfoPanelTab, ReactElement> = {
  sources: <LibraryBooks sx={{ fontSize: 16 }} />,
  method: <Route sx={{ fontSize: 16 }} />,
  privacy: <PrivacyTipOutlined sx={{ fontSize: 16 }} />,
};

const SCOUT_INFO_TAB_RAIL_ICON: Record<ScoutInfoPanelTab, ReactElement> = {
  sources: <LibraryBooks fontSize="small" />,
  method: <Route fontSize="small" />,
  privacy: <PrivacyTipOutlined fontSize="small" />,
};

const SCOUT_SIDE_PANEL_INFO_TABS: {
  value: ScoutInfoPanelTab;
  label: string;
  icon: ReactElement;
}[] = SCOUT_INFO_PANEL_TABS.map(({ value, label }) => ({
  value,
  label,
  icon: SCOUT_INFO_TAB_CHIP_ICON[value],
}));

type ScoutSidePanelProps = {
  open: boolean;
  view: ScoutSidePanelView;
  tab: ScoutInfoPanelTab | null;
  isPersistentMode: boolean;
  onTogglePanel: () => void;
  onOpenView: (view: ScoutSidePanelView) => void;
  onOpenSearch: () => void;
  onClose: () => void;
  onTabChange: (tab: ScoutInfoPanelTab) => void;
  onBackFromSearch: () => void;
};

export const RECENTS_PAGE_SIZE = 12;
const RECENTS_MAX_HEIGHT = "min(55%, 50vh)";
const SCROLL_BOUNDARY_TOLERANCE_PX = 1;

function allowsVerticalScroll(element: HTMLElement) {
  const overflowY = window.getComputedStyle(element).overflowY;
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
}

function getScrollableAncestorWithinPanel(target: EventTarget | null, boundary: HTMLElement) {
  if (!(target instanceof Element)) {
    return null;
  }

  let element: Element | null = target;
  while (element && boundary.contains(element)) {
    if (
      element instanceof HTMLElement &&
      allowsVerticalScroll(element) &&
      element.scrollHeight > element.clientHeight
    ) {
      return element;
    }

    if (element === boundary) {
      return null;
    }

    element = element.parentElement;
  }

  return null;
}

function canScrollWithDelta(element: HTMLElement, deltaY: number) {
  if (deltaY < 0) {
    return element.scrollTop > 0;
  }

  if (deltaY > 0) {
    return (
      element.scrollTop + element.clientHeight < element.scrollHeight - SCROLL_BOUNDARY_TOLERANCE_PX
    );
  }

  return true;
}

type ScoutConversationHeaderProps = {
  conversationTitle?: string;
  showTitle: boolean;
  allowMutations?: boolean;
  onAllowMutationsChange?: (next: boolean) => void;
  settingsDisabled?: boolean;
  hasScoutProAccess?: boolean;
  /** Extra content rendered at the right end, before the settings menu. */
  endContent?: ReactNode;
};

export function ScoutConversationHeader({
  conversationTitle,
  showTitle,
  allowMutations = false,
  onAllowMutationsChange,
  settingsDisabled = false,
  hasScoutProAccess = true,
  endContent,
}: ScoutConversationHeaderProps) {
  const title = conversationTitle?.trim();

  return (
    <Stack
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 1,
        pointerEvents: "auto",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          minWidth: 0,
          flex: 1,
          pr: 2,
          pl: 1,
        }}
      >
        {showTitle && title && (
          <Typography
            variant="h6"
            color="text.secondary"
            noWrap
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem" },
              fontWeight: 400,
              lineHeight: 1.4,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        )}
        {showTitle && !title && (
          <Skeleton
            variant="text"
            width={220}
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem" },
              lineHeight: 1.4,
            }}
          />
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
        {endContent}
        <ScoutConversationSettingsMenu
          allowMutations={allowMutations}
          onAllowMutationsChange={onAllowMutationsChange ?? (() => undefined)}
          disabled={settingsDisabled}
          hasScoutProAccess={hasScoutProAccess}
        />
      </Stack>
    </Stack>
  );
}

export function ScoutSidePanel({
  open,
  view,
  tab,
  isPersistentMode,
  onTogglePanel,
  onOpenView,
  onOpenSearch,
  onClose,
  onTabChange,
  onBackFromSearch,
}: ScoutSidePanelProps) {
  const theme = useTheme();
  const showRail = !open;
  const drawerBody = (
    <ScoutSidePanelDrawerContent
      open={open}
      view={view}
      tab={tab}
      isPersistentMode={isPersistentMode}
      onOpenSearch={onOpenSearch}
      onClose={onClose}
      onTabChange={onTabChange}
      onBackFromSearch={onBackFromSearch}
    />
  );

  // Mobile: temporary overlay drawer plus a rail when collapsed.
  if (!isPersistentMode) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexShrink: 0,
          height: "100%",
        }}
      >
        <Drawer
          anchor="right"
          variant="temporary"
          open={open}
          onClose={onClose}
          disableScrollLock
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            },
          }}
        >
          {drawerBody}
        </Drawer>

        {showRail ? (
          <ScoutSidePanelRail
            open={open}
            view={view}
            onTogglePanel={onTogglePanel}
            onOpenView={onOpenView}
            onOpenSearch={onOpenSearch}
          />
        ) : null}
      </Box>
    );
  }

  // Desktop: a single container whose width animates between the collapsed rail
  // and the expanded panel, crossfading their contents — mirrors LeftNavV2.
  const widthTransition = theme.transitions.create("width", {
    easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  });
  const opacityTransition = theme.transitions.create("opacity", {
    duration: theme.transitions.duration.shortest,
  });

  return (
    <Box
      sx={{
        position: "relative",
        flexShrink: 0,
        height: "100%",
        overflow: "hidden",
        width: open ? SCOUT_INFO_PANEL_WIDTH_PX : SCOUT_SIDE_RAIL_WIDTH_PX,
        transition: widthTransition,
        borderRight: 0.5,
        borderColor: "divider",
      }}
    >
      {/* The rail lives in normal flow so it always paints; the expanded panel
          fades in on top of it, mirroring LeftNavV2's collapse behavior. */}
      <ScoutSidePanelRail
        open={open}
        view={view}
        onTogglePanel={onTogglePanel}
        onOpenView={onOpenView}
        onOpenSearch={onOpenSearch}
      />

      <Box
        aria-hidden={!open}
        sx={{
          position: "absolute",
          inset: 0,
          width: SCOUT_INFO_PANEL_WIDTH_PX,
          bgcolor: "background.paper",
          borderLeft: 1,
          borderColor: "divider",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: opacityTransition,
        }}
      >
        {drawerBody}
      </Box>
    </Box>
  );
}

function ScoutSidePanelRail({
  open,
  view,
  onTogglePanel,
  onOpenView,
  onOpenSearch,
}: {
  open: boolean;
  view: ScoutSidePanelView;
  onTogglePanel: () => void;
  onOpenView: (view: ScoutSidePanelView) => void;
  onOpenSearch: () => void;
}) {
  const navigate = useNavigate();
  const { tenantId } = useParams();

  const handleNewChat = useCallback(() => {
    navigate(`/o/${tenantId}/scout`);
  }, [navigate, tenantId]);

  const railItems: {
    key: ScoutSidePanelView | "new-chat" | "search" | "front-chat";
    label: string;
    icon: ReactNode;
    onClick: () => void;
    selected?: boolean;
  }[] = [
    {
      key: "search",
      label: "Search chats",
      icon: <SearchIcon fontSize="small" />,
      onClick: onOpenSearch,
    },
    {
      key: "new-chat",
      label: "New chat",
      icon: <EditSquare fontSize="small" />,
      onClick: handleNewChat,
    },
    ...SCOUT_INFO_PANEL_TABS.map(({ value, label }) => ({
      key: value,
      label,
      icon: SCOUT_INFO_TAB_RAIL_ICON[value],
      onClick: () => onOpenView(value),
      selected: open && view === value,
    })),
    {
      key: "front-chat",
      label: "Chat with an Encamp Representative",
      icon: <SupportAgent fontSize="small" />,
      onClick: openFrontChatWithAiBotBypass,
    },
  ];

  return (
    <Stack
      component="nav"
      aria-label="Scout actions"
      sx={{
        width: SCOUT_SIDE_RAIL_WIDTH_PX,
        height: "100%",
        flexShrink: 0,
        alignItems: "center",
        py: 1.5,
        gap: 1.5,
        bgcolor: "background.paper",
        borderLeft: 1,
        borderColor: "divider",
      }}
    >
      <Tooltip title="Open panel" placement="left">
        <IconButton size="small" onClick={onTogglePanel} aria-label="Open panel">
          <VerticalSplitOutlined fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider sx={{ height: "2px", px: 2 }} />

      {railItems.map((item) => (
        <Tooltip key={item.key} title={item.label} placement="left">
          <IconButton
            size="small"
            onClick={item.onClick}
            aria-label={item.label}
            aria-pressed={item.selected}
            color={item.selected ? "primary" : "default"}
            sx={{
              ...(item.selected && {
                bgcolor: "action.selected",
              }),
            }}
          >
            {item.icon}
          </IconButton>
        </Tooltip>
      ))}
    </Stack>
  );
}

function ScoutSidePanelDrawerContent({
  open,
  view,
  tab,
  isPersistentMode,
  onOpenSearch,
  onClose,
  onTabChange,
  onBackFromSearch,
}: {
  open: boolean;
  view: ScoutSidePanelView;
  tab: ScoutInfoPanelTab | null;
  isPersistentMode: boolean;
  onOpenSearch: () => void;
  onClose: () => void;
  onTabChange: (tab: ScoutInfoPanelTab) => void;
  onBackFromSearch: () => void;
}) {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const searchInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const isSearchView = view === "search";
  const isInfoTabView = tab !== null;
  const hasSearchTerm = searchInput.trim().length > 0;

  const handleSelectThread = useCallback(
    (thread: ScoutThreadListItem) => {
      navigate(`/o/${tenantId}/scout/${thread.id}`);
      if (!isPersistentMode) {
        onClose();
      }
    },
    [isPersistentMode, navigate, onClose, tenantId]
  );

  const handleNewChat = useCallback(() => {
    navigate(`/o/${tenantId}/scout`);
    if (!isPersistentMode) {
      onClose();
    }
  }, [isPersistentMode, navigate, onClose, tenantId]);

  const handleInfoTabChange = useCallback(
    (nextTab: ScoutInfoPanelTab) => {
      onTabChange(nextTab);
    },
    [onTabChange]
  );

  // The right-side affordance in the search view: clear the term when there is
  // one, otherwise step back to the info view the user came from.
  const handleSearchBack = useCallback(() => {
    if (hasSearchTerm) {
      setSearchInput("");
      return;
    }
    onBackFromSearch();
  }, [hasSearchTerm, onBackFromSearch]);
  const handlePanelWheelCapture = useCallback((event: WheelEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.deltaY === 0) {
      return;
    }

    const scrollableAncestor = getScrollableAncestorWithinPanel(event.target, event.currentTarget);
    if (!scrollableAncestor || !canScrollWithDelta(scrollableAncestor, event.deltaY)) {
      event.preventDefault();
    }
  }, []);

  const focusSearchInput = useCallback(() => {
    const input = searchInputRef.current;
    if (!input) {
      return;
    }

    input.focus();
    const cursorPosition = input.value.length;
    input.setSelectionRange(cursorPosition, cursorPosition);
  }, []);

  // Focus the field whenever the embedded search view appears.
  useEffect(() => {
    if (!open || !isSearchView) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(focusSearchInput);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [focusSearchInput, isSearchView, open]);

  // Clear the query when the panel closes so it reopens with an empty search.
  // Adjust during render (per the React docs) rather than in an effect.
  const wasOpenRef = useRef(open);
  if (wasOpenRef.current !== open) {
    wasOpenRef.current = open;
    if (!open && searchInput !== "") {
      setSearchInput("");
    }
  }

  return (
    <Box
      onWheelCapture={handlePanelWheelCapture}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        width: "100%",
      }}
    >
      <ScoutSidePanelHeader
        searchInput={searchInput}
        isSearchView={isSearchView}
        hasSearchTerm={hasSearchTerm}
        inputRef={searchInputRef}
        onSearchInputChange={setSearchInput}
        onFocusSearch={onOpenSearch}
        onNewChat={handleNewChat}
        onSearchBack={handleSearchBack}
        onClose={onClose}
      />
      <Divider sx={{ mx: 2, mb: 1.5 }} />

      {isSearchView ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            pl: 1.5,
            pb: 2,
          }}
        >
          <ScoutSidePanelSearchContent
            open={open}
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            onSelectThread={handleSelectThread}
          />
        </Box>
      ) : (
        <>
          <ScoutSidePanelInfoTabBar tab={tab} onTabChange={handleInfoTabChange} />
          {isInfoTabView ? (
            <ScoutInfoPanelContentBody tab={tab} />
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                pb: 2,
              }}
            >
              <ScoutSidePanelRecentsContent open={open} onSelectThread={handleSelectThread} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function ScoutSidePanelHeader({
  searchInput,
  isSearchView,
  hasSearchTerm,
  inputRef,
  onSearchInputChange,
  onFocusSearch,
  onNewChat,
  onSearchBack,
  onClose,
}: {
  searchInput: string;
  isSearchView: boolean;
  hasSearchTerm: boolean;
  inputRef: React.Ref<HTMLInputElement | HTMLTextAreaElement | null>;
  onSearchInputChange: (value: string) => void;
  onFocusSearch: () => void;
  onNewChat: () => void;
  onSearchBack: () => void;
  onClose: () => void;
}) {
  const backLabel = hasSearchTerm ? "Clear search" : "Back";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minHeight: (theme) => theme.spacing(PANEL_HEADER_HEIGHT_SPACING),
        px: 1.5,
        flexShrink: 0,
      }}
    >
      <TextInput
        placeholder="Search chats"
        fullWidth
        inputRef={inputRef}
        value={isSearchView ? searchInput : ""}
        readOnly={!isSearchView}
        onChange={isSearchView ? (e) => onSearchInputChange(e.target.value) : undefined}
        // In info views the field is a trigger: click / Enter opens search.
        // (Using `onClick` rather than `onFocus` avoids re-opening the desktop
        // dialog when focus is restored to the field on dialog close.)
        onClick={isSearchView ? undefined : onFocusSearch}
        onKeyDown={
          isSearchView
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onFocusSearch();
                }
              }
        }
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        }
      />
      <Tooltip title="New chat">
        <IconButton size="small" onClick={onNewChat} aria-label="New chat">
          <EditSquare fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Chat with an Encamp Representative">
        <IconButton onClick={openFrontChatWithAiBotBypass}>
          <SupportAgent fontSize="small" />
        </IconButton>
      </Tooltip>
      {isSearchView ? (
        <Tooltip title={backLabel}>
          <IconButton size="small" onClick={onSearchBack} aria-label={backLabel}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Close panel">
          <IconButton size="small" onClick={onClose} aria-label="Close panel">
            <VerticalSplitOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function ScoutSidePanelInfoTabBar({
  tab,
  onTabChange,
}: {
  tab: ScoutInfoPanelTab | null;
  onTabChange: (tab: ScoutInfoPanelTab) => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Stack
      direction="row"
      role="tablist"
      aria-label="Scout information panel"
      useFlexGap
      sx={{ px: 2, pb: 1, flexShrink: 0, flexWrap: "wrap", gap: 1 }}
    >
      {SCOUT_SIDE_PANEL_INFO_TABS.map(({ value, label, icon }) => {
        const selected = tab === value;

        return (
          <StatusChip
            key={value}
            icon={icon}
            label={label}
            color={selected ? "info" : "default"}
            variant="outlined"
            size={isMobile ? "medium" : "small"}
            onClick={() => onTabChange(value)}
            role="tab"
            aria-selected={selected}
          />
        );
      })}
    </Stack>
  );
}

function ScoutSidePanelRecentsContent({
  open,
  onSelectThread,
}: {
  open: boolean;
  onSelectThread: (thread: ScoutThreadListItem) => void;
}) {
  const recents = useScoutThreadsSearch({
    enabled: open,
    pageSize: RECENTS_PAGE_SIZE,
    fetchPolicy: "cache-and-network",
  });

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, justifyContent: "space-between" }}>
      <Box sx={{ flex: 1, minHeight: 0 }} />
      <Box
        sx={{
          flex: "0 1 auto",
          minHeight: 0,
          maxHeight: RECENTS_MAX_HEIGHT,
          display: "flex",
          flexDirection: "column",
          pl: 1.5,
        }}
      >
        <ScoutThreadListSection
          heading="Recents"
          items={recents.items}
          loading={recents.isInitialLoading}
          hasMore={recents.hasMore}
          loadingMore={recents.isLoadingMore}
          emptyMessage="No recent threads."
          errorMessage={recents.errorMessage}
          onLoadMore={recents.loadMore}
          onSelectItem={onSelectThread}
          pageSize={RECENTS_PAGE_SIZE}
          compact
        />
      </Box>
    </Stack>
  );
}

function ScoutSidePanelSearchContent({
  open,
  searchInput,
  onSearchInputChange,
  onSelectThread,
}: {
  open: boolean;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSelectThread: (thread: ScoutThreadListItem) => void;
}) {
  const isSearching = searchInput.trim().length > 0;

  // Live search results reflect the latest server ordering (network-only).
  const results = useScoutThreadsSearch({
    enabled: open && isSearching,
    searchInput,
    onSearchInputChange,
    fetchPolicy: "network-only",
  });

  // Recents paint instantly from cache while the empty search view is shown.
  const recents = useScoutThreadsSearch({
    enabled: open && !isSearching,
    pageSize: RECENTS_PAGE_SIZE,
    fetchPolicy: "cache-and-network",
  });

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, justifyContent: "space-between" }}>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <ScoutThreadListSection
          heading="Search results"
          items={isSearching ? results.items : []}
          loading={isSearching && results.isInitialLoading}
          hasMore={isSearching && results.hasMore}
          loadingMore={isSearching && results.isLoadingMore}
          emptyMessage={isSearching ? "No threads found." : "Search your chats to see results."}
          errorMessage={isSearching ? results.errorMessage : undefined}
          onLoadMore={results.loadMore}
          onSelectItem={onSelectThread}
          compact
        />
      </Box>

      {!isSearching ? (
        <Box
          sx={{
            flex: "0 1 auto",
            minHeight: 0,
            maxHeight: RECENTS_MAX_HEIGHT,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ScoutThreadListSection
            heading="Recents"
            items={recents.items}
            loading={recents.isInitialLoading}
            hasMore={recents.hasMore}
            loadingMore={recents.isLoadingMore}
            emptyMessage="No recent threads."
            errorMessage={recents.errorMessage}
            onLoadMore={recents.loadMore}
            onSelectItem={onSelectThread}
            pageSize={RECENTS_PAGE_SIZE}
            compact
          />
        </Box>
      ) : null}
    </Stack>
  );
}
